import { OrgTypes, StakeholderTypes, VendorTypes } from 'src/Library/Constants';
import {
  internalSpocTooltip,
  modeOfFreightTooltip,
  renderGroups,
  renderShipperGroups,
  isShipperOrgFFTab,
  isFForgShipperTab,
  renderVendorRating,
} from './masters.common';

const tabColor = {
  FF: '#525CCA',
  CHA: '#52C41A',
  SHIPPER: '#16578B',
  CUSTOMER: '#52BBA5',
  SUPPLIER: '#d8c017',
  SHIPPING_LINE: '#1890FF',
  CHILD: '#FF8C00',
  ADDRESS: '#8E44AD',
};

const statusMapping = {
  ONBOARDING_AWAITED: 'created_at',
  PENDING_APPROVAL: 'created_at',
  LINKED: 'linked_at',
  DECLINED: 'declined_at',
};

const tabOrder = [
  'CHA',
  'SHIPPING_LINE',
  'FF',
  'SHIPPER',
  'CHILD',
  'SUPPLIER',
  'CUSTOMER',
  'PRODUCT',
  'ADDRESS',
  'DOCUMENT'
];

const masterCategoriesMap = {
  SHIPPER: 'Shipper',
  FF: 'Vendor',
  SHIPPING_LINE: 'Shipping Line',
  SUPPLIER: 'Supplier',
  CUSTOMER: 'Customer',
  CHA: 'CHA',
  PRODUCT: 'Products',
  ADDRESS: 'Address',
  DOCUMENT: 'Document',
  CHILD: 'Child Stakeholders',
};

export const VENDOR_ORG_TYPES = [
  OrgTypes.FF,
  OrgTypes.CHA,
  OrgTypes.CFS_CONTRACTOR,
  OrgTypes.ICD_CONTRACTOR,
  OrgTypes.BREAK_BULK,
  OrgTypes.TRANSPORTER,
  OrgTypes.SURVEYOR,
  OrgTypes.INTER_CARTING,
];

const orgLinkedStatusObj = {
  LINKED: 'Linked',
  INACTIVE: 'Inactive',
  ONBOARDING_AWAITED: 'Onboarding Awaited',
  VERIFICATION_PENDING : 'Verification Pending',
};

const orgLinkedStatusColor = {
  LINKED: '#52C41A',
  INACTIVE: '#F5222D',
  ONBOARDING_AWAITED: '#FAAD14',
  VERIFICATION_PENDING : '#FAAD14',
};

const supplierColumns = (orgType: string) => ({
  name: 'Supplier Name',
  stakeholderCode: 'Supplier Code',
  emailList: 'Admin Name (email)',
  internalSPOC: internalSpocTooltip(orgType),
  product: 'Product(s)'
});

const shipperColumns = (orgType: string) => ({  
  name: 'Shipper Name',
  emailList: 'Admin Name (email)',
  dsrRecipients : 'DSR Recipients',
  internalSPOC: internalSpocTooltip(orgType),
  autoDemurrageMailer : 'AutoDemurrage/ DetentionMailer',
  autoDsr: 'Send Automatic DSR',
  templateName: 'DSR Template',
  ...((orgType === 'FF') && {
    group: renderShipperGroups(),
  }),
  product: 'Product(s)'
})

const customerColumns = (orgType: string) => ({  
  name: 'Customer Name',
  stakeholderCode: 'Customer Code',
  emailList: 'Admin Name (email)',
  dsrRecipients : 'DSR Recipients',
  internalSPOC: internalSpocTooltip(orgType),
  autoDemurrageMailer : 'AutoDemurrage/ DetentionMailer',
  autoDsr: 'Send Auto DSR',
  templateName: 'DSR Template',
  ...((orgType === 'FF') && {
    group: renderShipperGroups(),
  }),
  product: 'Product(s)'
})

const selectedLinkedStatusColumns = (selectedLinkedStatus: LinkedStatusFilter) => ({
  name: 'Organisation Name',
  kyc: 'KYC',
  user: 'User',
  lastStatusUpdatedAt: 'Status',
  ...(allowedLinkedStatusActionColumns.includes(selectedLinkedStatus) && {
    connectionAction: 'Action'
  }),
});

const ffColumns = (orgType: string) => ({
  organisationName: 'Organisation Name',
  ...(orgType !== 'SHIPPING_LINE' && {
    stakeholderType: 'Vendor Type',
    preferredServiceType: 'Service Type',
    businessType: 'Business Type',
    modeOfFreightService: modeOfFreightTooltip(),
    group: renderGroups(),
    linkedStatus: 'Status',
    vendorRating: renderVendorRating(),
  }),
  ...((orgType === 'SHIPPING_LINE') && {
    internalPOC: internalSpocTooltip(orgType),
  }),
  ...((orgType === 'FF') && {
    internalPOC: internalSpocTooltip(orgType),
  }),
});

const shippingLineColumns = (orgType: string) => ({
  organisationName: 'Organisation Name',
  ...(orgType !== 'FF' && {
    preferredServiceType: 'Service Type',
    businessType: 'Business Type',
    modeOfFreightService: modeOfFreightTooltip(),
  }),
  ...(orgType === 'FF' && {
    internalPOC: internalSpocTooltip(orgType),
  }),
});

const chaColumns = {
  organisationName: 'Organisation Name',
  preferredServiceType: 'Service Type',
  businessType: 'Business Type',
  modeOfFreightService: modeOfFreightTooltip(),
};

const childColumns = {
  organisationName: 'Organisation Name',
  linkedStatus: 'Status',
}

const productColumns = (orgType: string) =>  ({
  internalName: 'Internal Product Name',
  referenceNumber: 'Reference Number',
  hsCode: 'HS Code',
  remark: 'Description',
  skuCode: 'SKU Code',
  ...(orgType === 'SHIPPER' && {
    customer: 'Customer',
    supplier: 'Supplier',
  }),
  ...(orgType === 'FF' && {
    shipper: 'Shipper',
  }),
  measurement_unit: 'Unit',
  status: 'Status',
  action: 'Action',
});

const addressColumns = {
  name: 'Name',
  location: 'CSF',
  locationType: 'Location Type',
  addressTypes: 'Address Type',
  referenceNumber: 'Reference Number',
  address: 'Address',
  city: 'City',
  state: 'State',
  pincode: 'Pincode',
  countryName: 'Country',
  landmark: 'Landmark',
  originPortId: 'Final POL',
  destinationPortId: 'Final POD',
  incoterm: 'Incoterm',
  action: 'Action'
}; 

const columnMappings = (tabId: string, orgType: string, selectedLinkedStatus: LinkedStatusFilter) => {
  let columns: any = {};
  switch (tabId) {
    case 'FF':  return ffColumns(orgType);
    case 'CHA': return chaColumns;
    case 'PRODUCT': columns = productColumns(orgType); break;
    case 'SHIPPING_LINE': return shippingLineColumns(orgType);
    case 'SHIPPER': columns = allowedSelectedLinkedStatus.includes(selectedLinkedStatus)
    ? selectedLinkedStatusColumns(selectedLinkedStatus) : shipperColumns(orgType); break;
    case 'CUSTOMER': columns = allowedSelectedLinkedStatus.includes(selectedLinkedStatus)
      ? selectedLinkedStatusColumns(selectedLinkedStatus)
      : customerColumns(orgType); break;
    case 'SUPPLIER': columns = supplierColumns(orgType);break;
    case 'DOCUMENT': columns = documentColumns(orgType); break;
    case 'CHILD': return childColumns;
    case 'ADDRESS': columns = addressColumns; break;
    default: return {};
  }
  columns = {
    'selected' : '',
    ...columns,
  }
  if (tabId === 'DOCUMENT') {
    delete columns.selected;
  }
  return columns;
}

export enum LinkedStatusFilter {
  ALL = 'ALL',
  REQUESTED = 'REQUESTED',
  LINKED = 'LINKED',
  REJECTED = 'REJECTED',
  BLACKLISTED = 'BLACKLISTED'
}

const allowedLinkedStatusActionColumns = [LinkedStatusFilter.REQUESTED, LinkedStatusFilter.BLACKLISTED];
const allowedSelectedLinkedStatus = [...allowedLinkedStatusActionColumns, LinkedStatusFilter.REJECTED];

export enum OrganisationOnboardingStatus {
  LINKED = "LINKED",
  MERGED = "MERGED",
  REQUESTED = "REQUESTED",
  PENDING_APPROVAL = "PENDING_APPROVAL",
  REJECTED_ME = "REJECTED_ME",
  REJECTED_BY_ME = "REJECTED_BY_ME",
  CANCELLED_ME = "CANCELLED_ME",
  CANCELLED_BY_ME = "CANCELLED_BY_ME",
  BLACKLISTED_ME = "BLACKLISTED_ME",
  BLACKLISTED_BY_ME = "BLACKLISTED_BY_ME",
  NON_LINKED = "NON_LINKED",
}

export enum StatusTransitionAction {
	APPROVE = 'APPROVE',
	REJECT = 'REJECT',
	BLACKLIST = 'BLACKLIST',
	UNBLOCK = 'UNBLOCK',
}

const ConnectionStatusToLinkedStatusKeyMap = {
  [OrganisationOnboardingStatus.LINKED]: LinkedStatusFilter.LINKED,
  [OrganisationOnboardingStatus.REQUESTED]: LinkedStatusFilter.REQUESTED,
  [OrganisationOnboardingStatus.REJECTED_ME]: LinkedStatusFilter.REJECTED,
  [OrganisationOnboardingStatus.BLACKLISTED_ME]: LinkedStatusFilter.REJECTED
}

export const getLinkedStatusFilters = (type: string, activeTab: string) => {
  return [
    ...(isShipperOrgFFTab(type, activeTab) ? [{
        key: LinkedStatusFilter.ALL,
        label: 'All'
      }] : []
    ),
    {
      key: LinkedStatusFilter.REQUESTED,
      label: 'Requested'
    },
    {
      key: LinkedStatusFilter.LINKED,
      label: 'Linked',
    },
    {
      key: LinkedStatusFilter.REJECTED,
      label: 'Rejected'
    },
    ...(isFForgShipperTab(type, activeTab) ? [{
        key: LinkedStatusFilter.BLACKLISTED,
        label: 'Blacklisted'
      }] : []
    )
  ];
}

const linkedStatusLookupMap = {
  REQUESTED: [OrganisationOnboardingStatus.REQUESTED, OrganisationOnboardingStatus.PENDING_APPROVAL],
  LINKED: [OrganisationOnboardingStatus.LINKED],
  REJECTED: [OrganisationOnboardingStatus.REJECTED_ME, OrganisationOnboardingStatus.REJECTED_BY_ME],
  BLACKLISTED: [OrganisationOnboardingStatus.BLACKLISTED_BY_ME, OrganisationOnboardingStatus.BLACKLISTED_ME],
}

enum KycRejectionReasonKeys {
  REJECT = 'REJECT',
  BLACKLIST = 'BLACKLIST'
}

const KycRejectionReasonOptions = {
  [KycRejectionReasonKeys.REJECT]: 'The KYC details are incomplete',
  [KycRejectionReasonKeys.BLACKLIST]: 'I want to Blacklist the organisation'
}

const BlacklistedMessage = `Blacklisted Shippers can not search sell rates.
  All special rates and existing contracts will be stopped.`;

const dsrHeadersName = {
  TRANSSHIPMENT: 'Transshipment',
  VESSEL_DETAILS: 'Vessel Details',
}
const ModeOfServicesFilters = [
  { key: 'FCL', label: 'FCL' },
  { key: 'LCL', label: 'LCL' },
  { key: 'AIR', label: 'Air' },
  { key: 'TRAILER', label: 'Trucker' },
];

const StakeholderTypeFilters = [
  { key: VendorTypes.FF, label: 'FF' },
  { key: VendorTypes.SHIPPING_LINE, label: 'Shipping Line' },
  { key: VendorTypes.CHA, label: 'CHA' },
  { key: VendorTypes.CFS_CONTRACTOR, label: 'CFS Contractor' },
  { key: VendorTypes.BREAK_BULK, label: 'Break Bulk' },
  { key: VendorTypes.TRANSPORTER, label: 'Transporter' },
  { key: VendorTypes.ICD_CONTRACTOR, label: 'ICD Contractor' },
  { key: VendorTypes.SURVEYOR, label: 'Surveyor' },
  { key: VendorTypes.INTER_CARTING, label: 'Inter Carting' },
];

const getVendorServiceDisplay = (mode: string): string => {
  if (mode === 'TRAILER') {
    return ModeOfServicesFilters.find((m) => m.key === 'TRAILER')?.label ?? mode;
  }
  return mode;
};

const VendorRatingToolTip = ['Bad', 'Good', 'Very Good', 'Excelent', 'God Mode'];

const documentColumns = (orgType: string) =>  ({
  documentName: 'Name',
  documentNumber: 'Number',
  issuerType: 'Issuer',
  internalPoc: 'Internal POC',
  validity: 'Validity',
  updatedAt: 'Last Updated At',
  status: 'Status',
  action: 'Action',
});

export {
  tabColor,
  tabOrder,
  statusMapping,
  columnMappings,
  orgLinkedStatusObj,
  orgLinkedStatusColor,
  masterCategoriesMap,
  linkedStatusLookupMap,
  KycRejectionReasonOptions,
  KycRejectionReasonKeys,
  BlacklistedMessage,
  ConnectionStatusToLinkedStatusKeyMap,
  dsrHeadersName,
  ModeOfServicesFilters,
  StakeholderTypeFilters,
  getVendorServiceDisplay,
  VendorRatingToolTip,
};
